/**
 * Phase FIRST-INV (Part B) — Record payment modal.
 *
 * Supports partial + full manual payments recorded against an issued
 * invoice. Hits POST /admin/invoices/{id}/record-payment.
 *
 * When the invoice has a security-deposit line item, we show a spec §3
 * warning callout explaining that only a FULL payment auto-records the
 * deposit on the rental.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, DollarSign } from "lucide-react";

const METHODS = [
    { value: "ach", label: "ACH" },
    { value: "wire", label: "Wire" },
    { value: "check", label: "Check" },
    { value: "card", label: "Credit card" },
    { value: "zelle", label: "Zelle" },
    { value: "other", label: "Other" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const money = (cents) => `$${(Number(cents || 0) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const dollarsFromCents = (c) => (Number(c || 0) / 100).toFixed(2);
const centsFromDollars = (d) => {
    const n = parseFloat(String(d).replace(/,/g, ""));
    if (!isFinite(n)) return 0;
    return Math.round(n * 100);
};

export default function RecordPaymentModal({ open, onOpenChange, invoice, onRecorded }) {
    const [method, setMethod] = useState("ach");
    const [paymentDate, setPaymentDate] = useState(todayIso());
    const [amountDollars, setAmountDollars] = useState("0.00");
    const [reference, setReference] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const total = Number(invoice?.total_cents || 0);
    const alreadyPaid = Number(invoice?.amount_paid_cents || 0);
    const remaining = Math.max(0, total - alreadyPaid);

    const hasDepositLine = useMemo(() => {
        if (!invoice) return false;
        for (const li of invoice.line_items || []) {
            const lt = (li.line_type || "").toLowerCase();
            if (lt === "security_deposit") return true;
        }
        for (const li of invoice.line_items || []) {
            const d = (li.description || "").toLowerCase();
            if (d.includes("deposit")) return true;
        }
        return false;
    }, [invoice]);

    useEffect(() => {
        if (!open) return;
        setMethod("ach");
        setPaymentDate(todayIso());
        setAmountDollars(dollarsFromCents(remaining));
        setReference("");
        setNotes("");
    }, [open, remaining]);

    const enteredCents = centsFromDollars(amountDollars);
    const willFullyPay = enteredCents === remaining && remaining > 0;

    const submit = async () => {
        if (!invoice?.id) return;
        if (enteredCents <= 0) { toast.error("Amount must be > $0.00"); return; }
        if (enteredCents > remaining) {
            toast.error(`Amount cannot exceed remaining balance (${money(remaining)}).`);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/admin/invoices/${invoice.id}/record-payment`, {
                amount_cents: enteredCents,
                payment_method: method,
                payment_date: paymentDate,
                payment_reference: reference.trim() || null,
                notes: notes.trim() || null,
            });
            const fullyPaid = data?.status === "paid";
            toast.success(
                `Payment of ${money(enteredCents)} recorded` +
                (fullyPaid && hasDepositLine
                    ? " · Security deposit auto-recorded on rental"
                    : fullyPaid
                        ? " · Invoice fully paid"
                        : " · Invoice now partial paid"),
            );
            if (typeof onRecorded === "function") onRecorded(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="record-payment-modal">
                <DialogHeader>
                    <DialogTitle>Record payment</DialogTitle>
                    <DialogDescription>
                        Invoice <strong>{invoice?.invoice_number}</strong> · Total {money(total)} · Paid {money(alreadyPaid)} · Remaining <strong>{money(remaining)}</strong>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    {hasDepositLine ? (
                        <div className="text-sm rounded-md bg-amber-50 border border-amber-200 p-3 flex items-start gap-2"
                            data-testid="rp-deposit-warning">
                            <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
                            <div className="text-amber-900">
                                <div className="font-semibold">This invoice includes a security deposit line item.</div>
                                <div className="text-xs mt-0.5">
                                    Marking it fully paid will <strong>auto-record the deposit</strong> in the rental&apos;s Security Deposit
                                    section. Partial payments will <strong>NOT</strong> record the deposit — the full invoice total must
                                    be received first.
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Payment method</Label>
                            <Select value={method} onValueChange={setMethod}>
                                <SelectTrigger data-testid="rp-method"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {METHODS.map((m) => (
                                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Payment date</Label>
                            <Input type="date" value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                                data-testid="rp-date" />
                        </div>
                    </div>
                    <div>
                        <Label>Amount ($)</Label>
                        <Input type="number" min={0} step="0.01"
                            value={amountDollars}
                            onChange={(e) => setAmountDollars(e.target.value)}
                            data-testid="rp-amount" />
                        <div className="mt-1 flex items-center justify-between text-xs">
                            <span className="text-slate-500">
                                {willFullyPay
                                    ? "This payment will fully pay the invoice."
                                    : `After this payment, ${money(Math.max(0, remaining - enteredCents))} will remain.`}
                            </span>
                            <button type="button"
                                onClick={() => setAmountDollars(dollarsFromCents(remaining))}
                                className="text-blue-900 hover:underline"
                                data-testid="rp-fill-remaining">
                                Fill remaining
                            </button>
                        </div>
                    </div>
                    <div>
                        <Label>Reference (check #, txn id, etc.)</Label>
                        <Input value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            data-testid="rp-reference"
                            placeholder="Optional" />
                    </div>
                    <div>
                        <Label>Notes</Label>
                        <Textarea rows={2} value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="rp-notes" placeholder="Optional" />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        data-testid="rp-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={submitting}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        data-testid="rp-submit">
                        <DollarSign size={14} />
                        {submitting ? "Recording…" : "Record payment"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
